import { getRouteApi } from "@tanstack/react-router"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import type { CandidatePoolFilters } from "@/features/guild-war/presentation/candidate-pool-filters"

import { defaultCandidatePoolFilters } from "@/features/guild-war/presentation/candidate-pool-filters"

type CandidatePoolUiState = {
  filters: CandidatePoolFilters
  page: number
  candidateSearch: string
  debouncedCandidateSearch: string
}

export const createCandidatePoolUiStore = () =>
  createStore<CandidatePoolUiState>(() => ({
    filters: { ...defaultCandidatePoolFilters },
    page: 1,
    candidateSearch: "",
    debouncedCandidateSearch: "",
  }))

export type CandidatePoolUiStore = ReturnType<typeof createCandidatePoolUiStore>

// LEGACY: Global singleton for backward compatibility during migration
// TODO: Remove after all components are migrated to route-scoped pattern
export const candidatePoolUiStore = createCandidatePoolUiStore()

export const setCandidatePoolFilter = <K extends keyof CandidatePoolFilters>(
  key: K,
  value: CandidatePoolFilters[K],
) =>
  candidatePoolUiStore.setState((state) => ({
    page: 1,
    filters: { ...state.filters, [key]: value },
  }))

export const setCandidatePoolPage = (page: number) =>
  candidatePoolUiStore.setState({ page })

export const setCandidatePoolSearch = (search: string) =>
  candidatePoolUiStore.setState({ candidateSearch: search })

export const setCandidatePoolDebouncedSearch = (search: string) =>
  candidatePoolUiStore.setState({ debouncedCandidateSearch: search })

export const resetCandidatePoolFilters = () =>
  candidatePoolUiStore.setState({
    filters: { ...defaultCandidatePoolFilters },
    page: 1,
  })

export const applyCandidatePoolPresetFilters = (
  filters: CandidatePoolFilters,
) =>
  candidatePoolUiStore.setState({
    filters: { ...filters, paths: [...filters.paths] },
    page: 1,
  })

export const useCandidatePoolFilters = () =>
  useStore(candidatePoolUiStore, (s) => s.filters)

export const useCandidatePoolSearch = () =>
  useStore(candidatePoolUiStore, (s) => s.candidateSearch)

export const useCandidatePoolPage = () =>
  useStore(candidatePoolUiStore, (s) => s.page)

export const useCandidatePoolDebouncedSearch = () =>
  useStore(candidatePoolUiStore, (s) => s.debouncedCandidateSearch)

// Route-scoped access
const candidatePoolRouteApi = getRouteApi(
  "/_authed/_admin/guild-war/candidate-pool",
)

export const useCandidatePoolRouteStore = () =>
  candidatePoolRouteApi.useRouteContext({
    select: (ctx) => ctx.candidatePoolStore,
  })
